'use client'

import { MerchFormDialog } from '@/components/forms/merch-form-dialog'
import {
  PremiumFeature,
  PremiumHeading,
  PremiumHero,
  PremiumIntro,
  PremiumSection,
} from '@/components/premium/premium-ui'
import { Button } from '@/components/ui/button'
import { Calendar, Gift, Globe2, Heart, Package, Sparkles, Users } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { toast } from 'sonner'

export const MerchPage = () => {
  const handleSuccess = () => {
    toast.success('Enquiry sent', { description: 'Our team will confirm stock, total cost, payment and dispatch details with you.' })
  }
  const handleError = () => {
    toast.error('Submission failed', { description: 'Please try again or contact support.' })
  }

  return (
    <div className="bg-background">
      <PremiumHero
        image="/merch.jpeg"
        imageAlt="Oorva handcrafted merchandise rooted in Tamil craft traditions"
        eyebrow="Oorva Store"
        title={
          <>
            Stories you
            <br />
            can touch.
          </>
        }
        subtitle="Carry a piece of the village with you. Curated keepsakes rooted in culture, craft and meaningful travel."
      />

      <PremiumSection>
        <div className="mx-auto max-w-3xl text-center">
          <PremiumHeading className="text-3xl md:text-5xl">Why we built this</PremiumHeading>
          <PremiumIntro className="mt-4">
            Oorva merch extends the journey beyond the visit and keeps the stories of local
            communities present in everyday life.
          </PremiumIntro>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-4">
          <PremiumFeature icon={<Heart className="h-5 w-5" />} title="Authentic connection" body="Bring Oorva experiences closer, even from home." />
          <PremiumFeature icon={<Users className="h-5 w-5" />} title="Support locals" body="Celebrate and support local crafts and traditions." />
          <PremiumFeature icon={<Globe2 className="h-5 w-5" />} title="Cultural reminder" body="A beautiful reminder of India's villages." />
          <PremiumFeature icon={<Sparkles className="h-5 w-5" />} title="Sustain impact" body="Connect travellers and communities beyond the visit." />
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="grid gap-8 rounded-[1.5rem] border border-border bg-card p-6 lg:grid-cols-2 lg:items-center lg:p-10">
          <div className="relative min-h-[340px] overflow-hidden rounded-[1.25rem] sm:min-h-[440px] lg:min-h-[500px]">
            <Image src="/merch.jpeg" alt="Experience Calendar 2026" fill className="object-cover" sizes="(min-width: 1024px) 48vw, 100vw" />
            <div className="absolute left-5 top-5 rounded-full bg-brand px-4 py-2 text-xs font-bold uppercase tracking-widest text-white">
              First launch
            </div>
          </div>
          <div>
            <div className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-accent-foreground">
              <Calendar className="h-5 w-5" /> Experience Calendar 2026
            </div>
            <PremiumHeading>A year of discovery on your desk.</PremiumHeading>
            <PremiumIntro className="mt-5">
              Featuring curated experiences across 12 districts of Tamil Nadu. Each month
              highlights a new craft, festival, food tradition or local story.
            </PremiumIntro>
            <div className="mt-8 flex items-center gap-6 rounded-2xl border border-border bg-background p-5 sm:p-6">
              <div>
                <p className="text-sm text-muted-foreground/70 line-through">Rs. 399</p>
                <p className="font-sans text-4xl font-semibold tracking-[-0.025em] text-accent-foreground">Rs. 299</p>
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="font-semibold text-foreground">Limited edition run</p>
                <p>Free shipping across India</p>
              </div>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">Send an enquiry to check stock. We will confirm the total, payment method and expected dispatch before you place an order. This form does not charge you.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <MerchFormDialog
                trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover"><Package className="mr-2 h-4 w-4" /> Enquire to order</Button>}
                onSuccess={handleSuccess}
                onError={handleError}
              />
              <Button asChild variant="outline" className="min-h-12 rounded-full border-border bg-transparent px-7 text-foreground hover:bg-secondary hover:text-foreground">
                <Link href="https://app.oorva.in" target="_blank" rel="noopener noreferrer">
                  Explore experiences
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection tone="forest">
        <div className="mx-auto max-w-3xl text-center">
          <Gift className="mx-auto h-12 w-12 text-brand-highlight" />
          <PremiumHeading className="mt-5 text-white">More coming soon</PremiumHeading>
          <p className="mt-4 text-white/68">
            Expect more authentic crafts, handlooms and cultural artifacts soon.
          </p>
        </div>
      </PremiumSection>
    </div>
  )
}
